import type { AnnotationLabelOption } from "./annotationLabels";
import { getAnnotationLabelColorStyle } from "./annotationLabelLayout";
import type { SpannedAnnotationEntity } from "./annotationSpans";
import type { EditableAnnotationEntity } from "./AnnotationTextEditor";
import { classNames } from "../../ui/styles/classNames";

type Span = SpannedAnnotationEntity<EditableAnnotationEntity>;

interface AnnotationSpanListProps {
  entities: readonly Span[];
  nameOf: (type: string) => string;
  /** The colours of each label key, as the text shows them. */
  optionOf: (type: string) => AnnotationLabelOption;
  /** Names the list, such as "Annotations in petition 1". */
  label: string;
  /** The ID of the element holding a span's note and problems, which its label tag refers
   * to; `undefined` for a span with neither. */
  noteIdOf: (span: Span) => string | undefined;
}

/**
 * The spans under the text, in text order: the complete account for screen readers.
 * Spans with a note (what the annotator wrote about them) or a problem (why the server
 * refused them) show with their label and quote, so they read without hovering; the
 * others are for screen readers only. A noted span's label tag in the text also takes
 * focus and is described by its note.
 */
export default function AnnotationSpanList({ entities, nameOf, optionOf, label, noteIdOf }: AnnotationSpanListProps) {
  if (entities.length === 0) {
    return null;
  }
  const inTextOrder = [...entities].sort((left, right) => left.start - right.start || left.end - right.end);
  const shown = (span: Span) => noteIdOf(span) !== undefined;
  return (
    <ul aria-label={label} className={inTextOrder.some(shown) ? "mt-2 space-y-1 text-xs" : "sr-only"}>
      {inTextOrder.map((span) => (
        <li key={span.id} className={classNames(!shown(span) && "sr-only")}>
          <span className="rounded-sm border px-1 py-px text-[10px] font-semibold" style={getAnnotationLabelColorStyle(optionOf(span.type))}>
            {nameOf(span.type)}
          </span>
          <span className="sr-only">: </span>
          <q className="ml-1">{span.value}</q>
          {shown(span) ? (
            <> <span id={noteIdOf(span)}>
              {span.note ? <span className="app-text-muted">{span.note}</span> : null}
              <SpanProblems reasons={span.problems} />
            </span></>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/** What the tooltip says about the spans under the pointer or focus: each label and quote, its note and problems. */
export function AnnotationTooltipText({ spans, nameOf }: { spans: readonly Span[]; nameOf: (type: string) => string }) {
  return (
    <span className="block space-y-1">
      {spans.map((span) => (
        <span key={span.id} className="block">
          <span className="font-semibold">{nameOf(span.type)}</span>: {span.value}
          {span.note ? <span className="app-text-muted block">{span.note}</span> : null}
          <SpanProblems reasons={span.problems} />
        </span>
      ))}
    </span>
  );
}

/** Why the server refused a span, one reason per line. */
export function SpanProblems({ reasons }: { reasons: readonly string[] | undefined }) {
  return reasons?.map((reason) => <span key={reason} className="app-error-text block">{reason}</span>) ?? null;
}
